import { useEffect, useState } from 'react';
import { ProductGrid } from '../components/ProductGrid';
import { CenterState, ErrorBanner, Screen, Spinner } from '../components/Ui';
import { ApiError, api } from '../lib/api';
import { notify, openShop } from '../lib/telegram';
import type { Card, Collection } from '../types';

type Props = {
  collectionId: string;
};

export function CollectionView({ collectionId }: Props) {
  const [collection, setCollection] = useState<Collection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyCardId, setBusyCardId] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const res = await api.getCollection(collectionId);
      setCollection(res.collection);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить подборку');
    }
  };

  useEffect(() => {
    void load();
  }, [collectionId]);

  const openCard = async (card: Card) => {
    setBusyCardId(card.id);
    try {
      // Клик считаем на сервере, он же отдаёт целевой URL — так ссылка
      // всегда актуальная, а аналитика не зависит от фронта.
      const res = await api.click(collectionId, card.id);
      openShop(res.url);
    } catch {
      notify('error');
      openShop(card.url); // не смогли записать клик — всё равно пускаем в магазин
    } finally {
      setBusyCardId(null);
    }
  };

  if (error) {
    return (
      <Screen>
        <CenterState>
          <ErrorBanner onRetry={() => void load()}>{error}</ErrorBanner>
        </CenterState>
      </Screen>
    );
  }

  if (!collection) {
    return (
      <Screen>
        <CenterState>
          <Spinner />
        </CenterState>
      </Screen>
    );
  }

  // Подписывается канал, а не автор: подписчик пришёл из канала и знает его.
  const channelName = collection.channel?.title ?? collection.channel?.username ?? null;

  return (
    // Витрина занимает экран целиком: сетка товаров должна помещаться
    // без прокрутки, а не уезжать вниз.
    <div className="screen screen--full">
      <header className="viewer-header">
        <div className="viewer-header__title">Модные вещички</div>
        {channelName && <div className="viewer-header__author">{channelName}</div>}
      </header>

      {/* Текст поста здесь не показываем — он остаётся в канале. */}
      <ProductGrid cards={collection.cards} onOpen={openCard} busyCardId={busyCardId} />
    </div>
  );
}
